import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  UploadDocumentMutation,
  UploadDocumentMutationVariables,
  UploadUrlQuery,
  UploadUrlQueryVariables,
} from "types/graphql";

interface UploadFileObject {
  signedRequest: string;
  file: File;
}

interface UploadCallbacks {
  onProgress: (percent: number, status: string, file: File) => void;
  onFinish: (file: File) => unknown;
  onError: (
    message: string,
    file: File,
    context?: ReturnType<typeof _getErrorRequestContext>
  ) => unknown;
}

function _getErrorRequestContext(xhr: XMLHttpRequest) {
  return {
    response: xhr.responseText,
    status: xhr.status,
    statusText: xhr.statusText,
    readyState: xhr.readyState,
  };
}

function scrubFilename(filename: string) {
  return filename.replace(/[^\w\d_\-.]+/gi, "");
}

// eslint-disable-next-line @typescript-eslint/require-await -- public API has always returned a Promise; the body is callback-driven XHR
async function uploadFile(
  this: { httprequest?: XMLHttpRequest },
  fileObject: UploadFileObject,
  callback: UploadCallbacks
) {
  const successResponses = [200, 201];

  const req = new XMLHttpRequest();
  req.open("PUT", fileObject.signedRequest);

  if (!req) {
    callback.onError("CORS not supported", fileObject.file);
  } else {
    req.onload = function (this: UploadCallbacks) {
      if (successResponses.indexOf(req.status) >= 0) {
        this.onProgress(100, "Complete", fileObject.file);
        return this.onFinish(fileObject.file);
      } else {
        return this.onError(
          "Upload error: " + req.status,
          fileObject.file,
          _getErrorRequestContext(req)
        );
      }
    }.bind(callback);

    req.onerror = function (this: UploadCallbacks) {
      return this.onError(
        "Request error",
        fileObject.file,
        _getErrorRequestContext(req)
      );
    }.bind(callback);

    req.upload.onprogress = function (this: UploadCallbacks, e: ProgressEvent) {
      let percentLoaded;
      if (e.lengthComputable) {
        percentLoaded = Math.round((e.loaded / e.total) * 100);
        return this.onProgress(
          percentLoaded,
          percentLoaded === 100 ? "Finalizing" : "Uploading",
          fileObject.file
        );
      } else {
        console.log(e.loaded);
      }
    }.bind(callback);
  }

  const disposition = "attachment";
  const fileName = scrubFilename(fileObject.file.name);

  req.setRequestHeader(
    "Content-Disposition",
    disposition + '; filename="' + fileName + '"'
  );
  req.setRequestHeader("Content-Type", fileObject.file.type);
  req.setRequestHeader("x-amz-server-side-encryption", "aws:kms");

  this.httprequest = req;
  return req.send(fileObject.file);
}

// get a signed url for uploading a document to s3
function getUploadUrl(
  client: ApolloClient<object>,
  fileName: string,
  fileType: string
): Promise<UploadUrlQuery["uploadUrl"]> {
  return new Promise((resolve, reject) => {
    const UPLOAD_URL = gql`
      query UploadUrl($fileName: String!, $fileType: String!) {
        uploadUrl(fileName: $fileName, fileType: $fileType) {
          signedRequest
          url
        }
      }
    `;
    const result = client.query<UploadUrlQuery, UploadUrlQueryVariables>({
      query: UPLOAD_URL,
      variables: { fileName, fileType },
    });
    result.then(
      (data) => {
        resolve(data.data.uploadUrl);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function uploadDocument(
  client: ApolloClient<object>,
  url: string,
  fileName: string,
  fileType: string,
  contentType: string
): Promise<UploadDocumentMutation["uploadDocument"] | null> {
  return new Promise((resolve, reject) => {
    const UPLOAD_DOCUMENT = gql`
      mutation UploadDocument(
        $url: String!
        $fileName: String!
        $fileType: String!
        $contentType: String!
      ) {
        uploadDocument(
          url: $url
          fileName: $fileName
          fileType: $fileType
          contentType: $contentType
        ) {
          id
          error
        }
      }
    `;
    const result = client.mutate<
      UploadDocumentMutation,
      UploadDocumentMutationVariables
    >({
      mutation: UPLOAD_DOCUMENT,
      variables: {
        url,
        fileName,
        fileType,
        contentType,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.uploadDocument ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export const fileService = {
  getUploadUrl,
  uploadFile,
  uploadDocument,
};
